import React, { useState } from 'react';
import { Eye, Edit, XCircle, CheckCircle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const ActiveTab = ({ 
  tournaments, 
  onCancel, 
  loading, 
  setLoading,
  user, 
  countdowns,
  commissionRates,
  language, 
  t,
  showSuccessToast,
  showErrorToast,
  showInfoToast,
  onTournamentUpdate,
  onUserUpdate
}) => {
  const [selectedWinners, setSelectedWinners] = useState({});
  const [editMatchIdModal, setEditMatchIdModal] = useState({
    show: false,
    tournament: null,
    loading: false
  });
  
  // Helper function to get commission info for display
  const getCommissionInfo = (tournament) => {
    const gameType = tournament.gameMode === 'battleRoyale' ? 'battleRoyale' : 'multiplayer';
    
    // Default commission rates
    let approverRate = gameType === 'battleRoyale' ? 12.5 : 25;
    let systemRate = gameType === 'battleRoyale' ? 12.5 : 25;
    
    // If tournament has stored commission rates, use those
    if (tournament.commissionRates) {
      approverRate = tournament.commissionRates.approver || approverRate;
      systemRate = tournament.commissionRates.system || systemRate;
    }
    
    const totalRate = approverRate + systemRate;
    
    return {
      approverRate,
      systemRate,
      totalRate,
      gameType
    };
  };
  
  // Helper function to calculate what approver/supervisor will earn
  const calculateApproverEarnings = (tournament) => {
    if (!tournament.approvedBy) return null;
    
    const commissionInfo = getCommissionInfo(tournament);
    
    // Calculate total entry fees
    let totalEntry = 0;
    if (tournament.tournamentType === 'giveaway') {
      totalEntry = tournament.prizePool || 0;
    } else {
      const participantEntries = (tournament.entryFee || 0) * (tournament.participants?.length || 0);
      const fillerEntries = (tournament.fillers || []).reduce((sum, f) => sum + (f.paidAmount || 0), 0);
      totalEntry = participantEntries + fillerEntries;
    }
    
    // Calculate commission amounts
    const approverCommission = Math.floor(totalEntry * commissionInfo.approverRate / 100);
    const systemCommission = Math.floor(totalEntry * commissionInfo.systemRate / 100);
    
    return {
      totalEntry,
      approverCommission,
      systemCommission,
      totalCommission: approverCommission + systemCommission,
      commissionInfo
    };
  };
  
  // Helper function to get all participants including fillers with enhanced data
  const getAllParticipants = (tournament) => {
    const realParticipants = (tournament.participants || []).map(p => ({
      _id: p._id,
      type: 'real',
      name: p.inGameName || p.username || 'Unknown',
      username: p.username,
      inGameName: p.inGameName,
      points: p.points || 0,
      wins: p.wins || 0,
      losses: p.losses || 0
    }));
    
    const fillerParticipants = (tournament.fillers || []).map(f => {
      // Extract addedBy info properly
      let addedByInfo = {
        _id: null,
        inGameName: 'Unknown',
        username: 'Unknown'
      };
      
      if (f.addedBy) {
        if (typeof f.addedBy === 'string') {
          // If addedBy is just an ID string
          addedByInfo._id = f.addedBy;
        } else if (typeof f.addedBy === 'object') {
          // If addedBy is populated
          addedByInfo = {
            _id: f.addedBy._id || f.addedBy,
            inGameName: f.addedBy.inGameName || 'Unknown',
            username: f.addedBy.username || 'Unknown'
          };
        }
      }
      
      return {
        _id: f._id,
        type: 'filler',
        name: f.name,
        username: f.name,
        inGameName: f.name,
        addedBy: addedByInfo,
        paidAmount: f.paidAmount || 0,
        createdAt: f.createdAt,
        points: 0, // Fillers don't have points
        wins: 0,
        losses: 0
      };
    });
    
    return [...realParticipants, ...fillerParticipants];
  };
  
// UPDATED: Calculate max winners for ALL game modes with flexible logic
const getMaxWinners = (tournament, adminOverride = null) => {
  console.log('🔍 ACTIVE TAB: Getting max winners for:', {
    gameMode: tournament?.gameMode,
    multiplayerType: tournament?.multiplayerType,
    participantCount: (tournament?.participants?.length || 0) + (tournament?.fillers?.length || 0),
    adminOverride: adminOverride,
    tournament: tournament
  });

  // If admin provides override, use it (with limits)
  if (adminOverride && adminOverride > 0) {
    const actualParticipants = (tournament?.participants?.length || 0) + (tournament?.fillers?.length || 0);
    const maxAllowed = Math.min(adminOverride, actualParticipants, 10); // Cap at 10 for sanity
    console.log(`✅ ACTIVE TAB: Admin override - returning ${maxAllowed} winners`);
    return maxAllowed;
  }

  // Battle Royale specific logic (keep existing)
  if (tournament.gameMode === 'battleRoyale') {
    console.log('🎯 ACTIVE TAB: Battle Royale detected, checking multiplayerType:', tournament.multiplayerType);
    
    if (tournament.multiplayerType === 'duo') {
      console.log('✅ ACTIVE TAB: Battle Royale Duo - returning 2 winners');
      return 2;
    }
    if (tournament.multiplayerType === 'squad') {
      console.log('✅ ACTIVE TAB: Battle Royale Squad - returning 4 winners');
      return 4;
    }
    if (tournament.multiplayerType === 'solo') {
      console.log('✅ ACTIVE TAB: Battle Royale Solo - returning 1 winner');
      return 1;
    }
    
    console.log('⚠️ ACTIVE TAB: Unknown Battle Royale type:', tournament.multiplayerType, 'defaulting to 1');
    return 1;
  }
  
  // NEW: All other game modes support flexible winners based on participant count
  const actualParticipants = (tournament?.participants?.length || 0) + (tournament?.fillers?.length || 0);
  
  if (actualParticipants <= 2) {
    console.log('ℹ️ ACTIVE TAB: Small tournament (≤2 players) - returning 1 winner');
    return 1;
  }
  
  // FIXED: Allow up to (participants - 1), with minimum 1 and maximum 5
  const flexibleMax = Math.max(1, Math.min(5, actualParticipants - 1));
  console.log(`✅ ACTIVE TAB: ${tournament.gameMode} tournament with ${actualParticipants} players - returning ${flexibleMax} max winners`);
  
  return flexibleMax;
};
  
  // Check if user can complete tournament
  const canCompleteTournament = (tournament) => {
    // Main admin can complete any tournament
    if (user.role === 'main_admin') return true;
    
    // Supervisors and approvers can complete tournaments they approved
    if (['supervisor', 'approver'].includes(user.role)) {
      // If tournament has an approver, only that approver can complete it
      if (tournament.approvedBy && tournament.approvedBy._id === user._id) {
        return true;
      }
      
      // If tournament has no approver but user is admin, allow completion (edge case)
      if (!tournament.approvedBy) {
        return true;
      }
    }
    
    return false;
  };

  // Check if user can see room ID
  const canSeeRoomId = (tournament) => {
    return tournament.participants.some(p => p._id === user._id);
  };

  // Copy match ID function
  const copyMatchId = (matchId) => {
    if (matchId) {
      navigator.clipboard.writeText(matchId);
      showSuccessToast(t('matchIdCopied', language));
    }
  };

  // Display functions for tournaments
  const getModeDisplay = (tournament) => {
    if (tournament.gameMode === 'multiplayer') {
      return tournament.multiplayerType?.toUpperCase() || 'MULTIPLAYER';
    }
    return tournament.gameMode?.toUpperCase() || 'BATTLE ROYALE';
  };

  const getCustomModeDisplay = (tournament) => {
    if (tournament.gameMode === 'multiplayer' && tournament.multiplayerType === 'custom') {
      return tournament.customGameMode || 'CUSTOM';
    }
    return null;
  };

  const getTitleDisplay = (tournament) => {
    const customMode = getCustomModeDisplay(tournament);
    if (customMode) {
      return `${tournament.creator.inGameName}'s ${customMode} ${t('tournaments', language)}`;
    }
    return `${tournament.creator.inGameName}'s ${getModeDisplay(tournament)} ${t('tournaments', language)}`;
  };

  const getCustomModeWarning = (tournament) => {
    if (tournament.gameMode === 'multiplayer' && tournament.multiplayerType === 'custom') {
      return t('customMode', language).toUpperCase();
    }
    return null;
  };

  const getTimeSince = (date) => {
    const now = new Date();
    const diffMs = now - new Date(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}${t('daysShort', language)} ${t('ago', language)}`;
    if (diffHours > 0) return `${diffHours}${t('hrs', language)} ${t('ago', language)}`;
    if (diffMins > 0) return `${diffMins}${t('mins', language)} ${t('ago', language)}`;
    return t('now', language);
  };
  
  // UPDATED: Toggle winner with multi-winner support for all game modes
  const toggleWinner = (tournamentId, participantId) => {
    setSelectedWinners(prev => {
      const current = prev[tournamentId] || [];
      const tournament = tournaments.find(t => t._id === tournamentId);
      const maxWinners = getMaxWinners(tournament);
      
      console.log('🎯 ACTIVE TAB: Toggling winner:', {
        tournamentId,
        participantId,
        currentSelected: current,
        maxWinners,
        gameMode: tournament.gameMode,
        participantCount: (tournament.participants?.length || 0) + (tournament.fillers?.length || 0),
        isAlreadySelected: current.includes(participantId)
      });
      
      if (current.includes(participantId)) {
        // Remove winner
        const newSelection = current.filter(id => id !== participantId);
        console.log('✅ ACTIVE TAB: Removing winner, new selection:', newSelection);
        return { ...prev, [tournamentId]: newSelection };
      } else if (current.length < maxWinners) {
        // Add winner
        const newSelection = [...current, participantId];
        console.log('✅ ACTIVE TAB: Adding winner, new selection:', newSelection);
        return { ...prev, [tournamentId]: newSelection };
      } else {
        console.log('⚠️ ACTIVE TAB: Cannot add winner - max reached:', maxWinners);
        // Show feedback when max reached
        toast(`Maximum ${maxWinners} winners allowed for this tournament (${tournament.gameMode} with ${(tournament.participants?.length || 0) + (tournament.fillers?.length || 0)} players)`, {
          icon: '⚠️',
          duration: 4000,
        });
      }
      
      return prev;
    });
  };

  const handleCompleteMatch = (tournamentId, winnerIds) => {
    const tournament = tournaments.find(t => t._id === tournamentId);
    
    // Client-side authorization check
    if (!canCompleteTournament(tournament)) {
      toast.error(t('insufficientPermissions', language) || 'You do not have permission to complete this tournament.');
      return;
    }
    
    if (!winnerIds || winnerIds.length === 0) {
      toast.error(t('pleaseSelectWinner', language) || 'Please select at least one winner.');
      return;
    }
    
    completeMatch(tournamentId, winnerIds);
    setSelectedWinners(prev => ({ ...prev, [tournamentId]: [] }));
  };

  // ENHANCED: Complete match function with comprehensive filler support and error handling
  const completeMatch = async (tournamentId, winnerIds) => {
    if (!winnerIds || winnerIds.length === 0) {
      showErrorToast(t('pleaseSelectWinner', language));
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('🏆 Completing tournament with ALL game modes multi-winner support:', {
        tournamentId,
        winnerIds,
        userRole: user.role
      });
      
      // Show progress toast
      const progressToast = toast.loading(t('completing', language) || 'Completing tournament...');
      
      // Use the filler-aware completion endpoint with timeout
      const response = await axios.post(`/api/tournaments/${tournamentId}/complete-with-fillers`, { 
        winnerIds 
      }, {
        timeout: 30000 // 30 second timeout
      });
      
      // Dismiss progress toast
      toast.dismiss(progressToast);
      
      // Success message with multi-winner info
      const winnersCount = response.data.results?.winnersCount || winnerIds.length;
      const prizePerWinner = response.data.results?.basePrizePerWinner || 0;
      
      let successMessage = t('matchCompletedSuccessfully', language);
      if (winnersCount > 1) {
        successMessage = `Tournament completed! ${winnersCount} winners each received ${prizePerWinner} points (Math.floor applied)`;
      }
      
      showSuccessToast(successMessage);
      
      // Handle filler wins notification with custom toast styling
      if (response.data.results && response.data.results.fillersWon > 0) {
        toast(
          `🤖 ${response.data.results.fillersWon} filler(s) won! Prizes awarded to their owners.`,
          {
            duration: 8000,
            icon: '🤖',
            style: {
              background: '#3b82f6',
              color: 'white',
              borderRadius: '8px',
            },
          }
        );
        
        // Show individual filler wins with staggered timing
        if (response.data.prizeDistribution) {
          response.data.prizeDistribution.forEach((prize, index) => {
            if (prize.winnerType === 'filler') {
              setTimeout(() => {
                showSuccessToast(
                  `🎉 Filler "${prize.winnerName}" won! Prize awarded to ${prize.awardedToName}`,
                  6000
                );
              }, index * 1000); // Stagger the notifications
            }
          });
        }
      }
      
      // Show commission information if available
      if (response.data.commission && response.data.commission.distributed) {
        setTimeout(() => {
          toast(
            `💰 Commission distributed: ${response.data.commission.totalCommission || 0} points`,
            {
              duration: 5000,
              icon: '💰',
              style: {
                background: '#8b5cf6',
                color: 'white',
                borderRadius: '8px',
              },
            }
          );
        }, 2000);
      }
      
      // Show Math.floor info for multi-winner tournaments
      if (winnersCount > 1 && response.data.results?.flooredDistribution) {
        setTimeout(() => {
          showInfoToast(
            `Prize per winner calculated with Math.floor (e.g., 145.5 → 145)`,
            4000
          );
        }, 1500);
      }
      
      // Show detailed results summary
      if (response.data.results) {
        const { winnersCount, totalPrizeDistributed, fillersWon = 0, realPlayersWon = 0 } = response.data.results;
        
        setTimeout(() => {
          showInfoToast(
            `Tournament completed: ${realPlayersWon} real player(s) + ${fillersWon} filler(s) won ${totalPrizeDistributed} total points`,
            6000
          );
        }, 1000);
      }
      
      onTournamentUpdate();
      onUserUpdate();
      
    } catch (error) {
      console.error('❌ Complete match error:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = t('failedToCompleteMatch', language) || 'Failed to complete tournament';
      let errorDetails = '';
      
      if (error.response) {
        const { status, data } = error.response;
        
        switch (status) {
          case 400:
            errorMessage = data.message || 'Invalid request - please check your selection';
            if (data.step) {
              errorDetails = `Error in step: ${data.step}`;
            }
            break;
            
          case 403:
            errorMessage = 'You do not have permission to complete this tournament';
            if (data.approvedBy) {
              errorDetails = `Only ${data.approvedBy} can complete this tournament`;
            }
            break;
            
          case 404:
            errorMessage = 'Tournament not found';
            break;
            
          case 500:
            errorMessage = 'Server error during completion';
            if (data.step) {
              errorDetails = `Failed at step: ${data.step}`;
              console.log('Server error details:', data);
            }
            
            // Show specific error messages based on step
            if (data.step === 'model_validation') {
              errorMessage = 'Database connection error - please try again';
            } else if (data.step === 'tournament_lookup') {
              errorMessage = 'Could not find tournament - please refresh the page';
            } else if (data.step === 'population') {
              errorMessage = 'Error loading tournament data - please try again';
            } else if (data.step === 'participant_processing') {
              errorMessage = 'Error processing participants - please contact support';
            } else if (data.step === 'manual_completion') {
              errorMessage = 'Error during completion process - please try again';
            }
            break;
            
          default:
            errorMessage = `Server error (${status}) - please try again`;
        }
        
        // Log detailed error info for debugging
        if (data.debug) {
          console.log('🐛 Debug info:', data.debug);
        }
        
      } else if (error.request) {
        errorMessage = 'Network error - please check your connection';
        errorDetails = 'No response from server';
      } else {
        errorMessage = 'Unexpected error occurred';
        errorDetails = error.message;
      }
      
      // Show error toast with details
      showErrorToast(errorMessage, 8000);
      
      if (errorDetails) {
        setTimeout(() => {
          showErrorToast(`Details: ${errorDetails}`, 6000);
        }, 500);
      }
      
      // Show retry suggestion for server errors
      if (error.response?.status >= 500) {
        setTimeout(() => {
          showInfoToast('💡 Try refreshing the page or contact support if the issue persists', 10000);
        }, 2000);
      }
      
    } finally {
      setLoading(false);
    }
  };

  // Edit Match ID functions
  const handleEditMatchId = async (tournament, newMatchId, roomUrl) => {
    setEditMatchIdModal(prev => ({ ...prev, loading: true }));

    try {
      const response = await axios.post(`/api/admin/tournaments/${tournament._id}/edit-match-id`, {
        matchId: newMatchId,
        roomUrl: roomUrl || ''
      });

      showSuccessToast(t('matchIdUpdatedSuccessfully', language));
      setEditMatchIdModal({ show: false, tournament: null, loading: false });

      // Refresh data
      onTournamentUpdate();
      onUserUpdate();

    } catch (error) {
      const message = error.response?.data?.message || t('failedToUpdateMatchId', language);
      showErrorToast(message);
      setEditMatchIdModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Open edit match ID modal
  const openEditMatchIdModal = (tournament) => {
    setEditMatchIdModal({
      show: true,
      tournament,
      loading: false
    });
  };

  // Close edit match ID modal
  const closeEditMatchIdModal = () => {
    setEditMatchIdModal({
      show: false,
      tournament: null,
      loading: false
    });
  };

  return (
    <div className="tournament-section">
      <div className="tournament-section-header">
        <h4 className="tournament-section-title">{t('activeMatches', language)}</h4>
        {user.role !== 'main_admin' && (
          <div className="tournament-info-panel">
            <p className="tournament-info-text">
              {t('youCanOnlyManageMatches', language)}
            </p>
          </div>
        )}
      </div>
      
      {tournaments.length === 0 ? (
        <div className="tournament-empty">
          <p className="tournament-empty-text">{t('noActiveMatches', language)}</p>
          {user.role !== 'main_admin' && (
            <p className="tournament-empty-subtext">{t('haventApprovedMatches', language)}</p>
          )}
        </div>
      ) : (
        tournaments.map(tournament => {
          const isParticipant = tournament.participants.some(p => p._id === user._id);
          const isGiveaway = tournament.tournamentType === 'giveaway';
          const isHighPrize = tournament.prizePool >= 500;
          const canEditMatchId = user.role === 'main_admin' || (tournament.approvedBy && tournament.approvedBy._id === user._id);
          
          const canComplete = canCompleteTournament(tournament);
          
          const maxWinners = getMaxWinners(tournament);
          const selectedWinnersForTournament = selectedWinners[tournament._id] || [];
          
          // Get all participants including fillers
          const allParticipants = getAllParticipants(tournament);
          const hasFillers = (tournament.fillers || []).length > 0;
          
          // Get commission earnings info
          const commissionEarnings = calculateApproverEarnings(tournament);

          console.log('🎯 ACTIVE TAB: Rendering tournament:', {
            id: tournament._id,
            gameMode: tournament.gameMode,
            multiplayerType: tournament.multiplayerType,
            participantCount: allParticipants.length,
            maxWinners,
            selectedCount: selectedWinnersForTournament.length,
            canSelectMultiple: maxWinners > 1
          });

          return (
            <div key={tournament._id} className="tournament-item blue">
              {/* Giveaway Badge */}
              {isGiveaway && (
                <div className="tournament-giveaway-badge">
                  <span>🎁</span>
                  <span>{t('giveaway', language)}</span>
                </div>
              )}
              
              {/* Room ID Section - Styled like bracket matches */}
              {(tournament.status === 'countdown' || tournament.status === 'active') && (
                <div className="admin-room-section">
                  {canSeeRoomId(tournament) ? (
                    <>
                      <div className="admin-room-info">
                        <span className="admin-room-label">🎮 {t('roomId', language) || 'Room'}:</span>
                        <span className="admin-room-value">{tournament.matchId || t('generating', language)}</span>
                      </div>
                      <div className="admin-room-actions">
                        <button
                          onClick={() => copyMatchId(tournament.matchId)}
                          className="admin-room-btn admin-room-btn-copy"
                          disabled={!tournament.matchId}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>{t('copyId', language)}</span>
                        </button>
                        {tournament.roomUrl && (
                          <button
                            onClick={() => window.open(tournament.roomUrl, '_blank')}
                            className="admin-room-btn admin-room-btn-join"
                            title={t('joinRoom', language) || 'Join Room'}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>{t('joinRoom', language) || 'Join'}</span>
                          </button>
                        )}
                        {canEditMatchId && tournament.matchId && (
                          <button
                            onClick={() => openEditMatchIdModal(tournament)}
                            className="admin-room-btn admin-room-btn-edit"
                            title={t('edit', language)}
                          >
                            <Edit className="w-3.5 h-3.5" />
                            <span>{t('edit', language)}</span>
                          </button>
                        )}
                        {onCancel && tournament.tournamentType !== 'bracket' && (
                          <button
                            onClick={() => onCancel(tournament)}
                            className="admin-room-btn admin-room-btn-cancel"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            <span>{t('cancel', language)}</span>
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="admin-room-locked">
                      <span>🔒 {t('noAccess', language)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="tournament-header">
                <div className="tournament-info">
                  <h5 className="tournament-title" style={{ color: isGiveaway ? '#fef3c7' : isHighPrize ? '#fecaca' : 'white' }}>
                    {getTitleDisplay(tournament)}
                    {getCustomModeWarning(tournament) && (
                      <span className="text-red-400 font-bold ml-2 text-xs">
                        ⚠️{getCustomModeWarning(tournament)}
                      </span>
                    )}
                  </h5>
                  
                  <div className="tournament-details blue">
                    <p>🎮 {getModeDisplay(tournament)} • 🗺️ {tournament.map} • 👥 {allParticipants.length}/{tournament.maxPlayers}</p>
                    {/* Show filler count if any */}
                    {hasFillers && (
                      <p className="text-blue-400">🤖 {(tournament.fillers || []).length} {t('fillers', language) || 'Fillers'}</p>
                    )}
                    <p>💰 {t('entry', language)}: {tournament.entryFee} • 🏆 {t('prize', language)}: {tournament.prizePool}</p>
                    
                    {/* Show commission info for approver/supervisor */}
                    {canComplete && commissionEarnings && (
                      <div className="commission-preview bg-green-900/20 border border-green-600/50 rounded p-2 mt-2">
                        <p className="text-green-400 text-xs font-medium">
                          💰 When you complete this tournament, you'll earn {commissionEarnings.approverCommission} points in commission 
                          ({commissionEarnings.commissionInfo.approverRate}% of {commissionEarnings.totalEntry} total entry fees)
                        </p>
                        {commissionEarnings.commissionInfo.gameType === 'battleRoyale' && (
                          <p className="text-blue-400 text-xs">🏆 Battle Royale commission rates applied</p>
                        )}
                      </div>
                    )}
                    
                    {tournament.status === 'countdown' && countdowns[tournament._id] && (
                      <p className="text-red-400 font-black animate-pulse">⏱️ {t('startsIn', language)}: {countdowns[tournament._id]}</p>
                    )}
                    <p className="text-blue-400">🕐 {t('created', language)}: {getTimeSince(tournament.createdAt)}</p>
                  </div>
                </div>

                <div className="tournament-status-info">
                  <div className={`tournament-status-badge ${tournament.status === 'countdown' ? '' : 'green'}`}>
                    <p className={`tournament-status-text ${tournament.status === 'countdown' ? '' : 'green'}`}>
                      {tournament.status === 'countdown' ? t('standby', language) : t('activeBadge', language)}
                    </p>
                  </div>

                  {isParticipant && (
                    <div className="tournament-status-badge green">
                      <p className="tournament-status-text green">{t('joined', language)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Winner Selection Section - Show ONLY if user can complete */}
              {canComplete ? (
                <div className="tournament-participants">
                  <p className="tournament-participants-title blue">
                    {maxWinners > 1 ? `${t('selectWinnersPlural', language) || 'Select Winners'} (${selectedWinnersForTournament.length}/${maxWinners})` : `${t('selectWinners', language) || 'Select Winner'} (${selectedWinnersForTournament.length}/${maxWinners})`}:
                    {tournament.status === 'countdown' && (
                      <span className="text-yellow-400 text-sm ml-2">
                        {t('readyForSelection', language) || 'Ready for selection'}
                      </span>
                    )}
                    {/* Show filler info */}
                    {hasFillers && (
                      <span className="text-blue-400 text-sm ml-2">
                        (Includes {(tournament.fillers || []).length} AI fillers)
                      </span>
                    )}
                    {/* UPDATED: Show multi-winner info for ALL game modes */}
                    {maxWinners > 1 && (
                      <span className="text-green-400 text-sm ml-2">
                        ({tournament.gameMode === 'battleRoyale' ? `${tournament.multiplayerType} mode` : `${tournament.gameMode} mode`} - up to {maxWinners} winners, Math.floor applied)
                      </span>
                    )}
                  </p>
                  <div className="participants-grid">
                    {/* Show all participants including fillers with proper names */}
                    {allParticipants.map(participant => {
                      const isSelected = selectedWinnersForTournament.includes(participant._id);
                      const isFiller = participant.type === 'filler';
                      const canSelect = !isSelected && selectedWinnersForTournament.length < maxWinners;
                      
                      return (
                        <button
                          key={participant._id}
                          onClick={() => toggleWinner(tournament._id, participant._id)}
                          disabled={loading || (!isSelected && selectedWinnersForTournament.length >= maxWinners)}
                          className={`participant-btn ${isSelected ? 'selected' : 'unselected'} ${isFiller ? 'filler-participant' : ''}`}
                          style={isFiller ? {
                            background: isSelected 
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(99, 102, 241, 0.5))'
                              : 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(99, 102, 241, 0.2))',
                            border: isSelected ? '2px solid #3b82f6' : '1px solid #3b82f6',
                            color: '#93c5fd'
                          } : {}}
                        >
                          <span className="font-bold">
                            {isSelected ? '👑' : (isFiller ? '🤖' : '👤')} {participant.name}
                            {/* Show player name who added filler instead of "(AI Filler)" */}
                            {isFiller && ` (${participant.addedBy?.inGameName || participant.addedBy?.username || 'Unknown'})`}
                          </span>
                          <span className="participant-name">
                            {isFiller 
                              ? `Added by ${participant.addedBy?.inGameName || participant.addedBy?.username || 'Unknown'} • Prize goes to them`
                              : `${participant.points} ${t('pts', language) || 'pts'} • ${participant.wins}W-${participant.losses}L`
                            }
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  
                  {selectedWinnersForTournament.length > 0 && (
                    <div className="tournament-actions">
                      <button
                        onClick={() => handleCompleteMatch(tournament._id, selectedWinnersForTournament)}
                        disabled={loading || selectedWinnersForTournament.length === 0}
                        className="tournament-complete-btn"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {loading ? t('completing', language) || 'Completing...' : 
                          `${t('completeMatch', language) || 'Complete Match'} (${selectedWinnersForTournament.length} ${selectedWinnersForTournament.length > 1 ? t('winners', language) || 'winners' : t('winner', language) || 'winner'})`
                        }
                      </button>
                    </div>
                  )}

                  {/* UPDATED: Multi-winner info for ALL game modes */}
                  {maxWinners > 1 && (
                    <p className="text-blue-400 text-xs mt-2">
                      ℹ️ {tournament.gameMode === 'battleRoyale' 
                        ? `Battle Royale ${tournament.multiplayerType} mode allows up to ${maxWinners} winners`
                        : `${tournament.gameMode} tournament with ${allParticipants.length} players allows up to ${maxWinners} winners`
                      }. Prize calculated using Math.floor.
                    </p>
                  )}

                  {/* Filler information */}
                  {hasFillers && (
                    <div className="filler-info bg-blue-900/20 border border-blue-600/50 rounded p-2 mt-2">
                      <p className="text-blue-400 text-xs font-medium">
                        🤖 This tournament has {(tournament.fillers || []).length} AI filler(s). If a filler wins, the prize will be awarded to the person who added them.
                      </p>
                    </div>
                  )}

                  {tournament.status === 'countdown' && (
                    <p className="text-yellow-400 text-xs mt-2">
                      {t('winnerSelectionAvailable', language) || 'Winner selection is now available'}
                    </p>
                  )}
                </div>
              ) : (
                // Show message if user cannot complete tournament
                ['supervisor', 'approver'].includes(user.role) && (
                  <div className="tournament-participants">
                    <div className="tournament-no-access bg-orange-900/20 border border-orange-600/50 rounded p-3">
                      <p className="text-orange-400 text-sm font-medium">
                        ⚠️ {tournament.approvedBy && tournament.approvedBy._id !== user._id 
                          ? `Only ${tournament.approvedBy.username || 'the approver'} can complete this tournament`
                          : 'You cannot complete this tournament'
                        }
                      </p>
                      {tournament.approvedBy && (
                        <p className="text-gray-400 text-xs mt-1">
                          Approved by: {tournament.approvedBy.username || 'Unknown'} ({tournament.approvedBy.role || 'Unknown'})
                        </p>
                      )}
                      {/* Show filler info even if can't complete */}
                      {hasFillers && (
                        <p className="text-blue-400 text-xs mt-1">
                          🤖 This tournament has {(tournament.fillers || []).length} AI filler(s)
                        </p>
                      )}
                      {/* Show max winners info */}
                      {maxWinners > 1 && (
                        <p className="text-green-400 text-xs mt-1">
                          🏆 This {tournament.gameMode === 'battleRoyale' ? `Battle Royale ${tournament.multiplayerType}` : tournament.gameMode} tournament supports up to {maxWinners} winners
                        </p>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          );
        })
      )}

      {/* Edit Match ID Modal */}
      {editMatchIdModal.show && (
        <EditMatchIdModal
          tournament={editMatchIdModal.tournament}
          onConfirm={(newMatchId, roomUrl) => handleEditMatchId(editMatchIdModal.tournament, newMatchId, roomUrl)}
          onCancel={closeEditMatchIdModal}
          loading={editMatchIdModal.loading}
          language={language}
          t={t}
        />
      )}
    </div>
  );
};

// Edit Match ID Modal Component
const EditMatchIdModal = ({ tournament, onConfirm, onCancel, loading, language, t }) => {
  const [newMatchId, setNewMatchId] = useState(tournament?.matchId || '');
  const [roomUrl, setRoomUrl] = useState(tournament?.roomUrl || '');
  const [isValid, setIsValid] = useState(true);

  React.useEffect(() => {
    if (tournament) {
      setNewMatchId(tournament.matchId || '');
      setRoomUrl(tournament.roomUrl || '');
    }
  }, [tournament]);

  const handleMatchIdChange = (e) => {
    const value = e.target.value;
    setNewMatchId(value);

    // Validation
    const trimmed = value.trim();
    setIsValid(trimmed.length > 0 && trimmed.length <= 50);
  };

  const handleRoomUrlChange = (e) => {
    setRoomUrl(e.target.value);
  };

  const hasChanges = () => {
    return newMatchId.trim() !== tournament?.matchId || roomUrl.trim() !== (tournament?.roomUrl || '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid && hasChanges()) {
      onConfirm(newMatchId.trim(), roomUrl.trim());
    }
  };

  if (!tournament) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="modal-icon">
            <Edit className="w-6 h-6 text-white" />
          </div>
          <h3 className="modal-title">{t('editMatchId', language)}</h3>
        </div>
        
        <div className="modal-body">
          <div className="modal-user-info">
            <p className="modal-user-name">{tournament.creator?.inGameName || 'Unknown'}'s {tournament.gameMode} {t('tournaments', language)}</p>
            <p className="modal-user-email">{t('status', language)}: {tournament.status}</p>
            <p className="modal-user-email">{t('participants', language)}: {tournament.participants?.length || 0}/{tournament.maxPlayers}</p>
            <p className="modal-user-email">{t('currentMatchId', language) || 'Current Match ID'}: <span className="font-mono font-bold">{tournament.matchId}</span></p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="role-reason-group">
              <label className="role-reason-label">
                {t('newMatchId', language) || 'New Match ID'}
              </label>
              <input
                type="text"
                value={newMatchId}
                onChange={handleMatchIdChange}
                placeholder={t('enterNewMatchId', language) || 'Enter new match ID'}
                className="role-search-input"
                maxLength="50"
                disabled={loading}
                autoFocus
              />
              
              {newMatchId.trim().length === 0 && (
                <p className="text-red-400 text-xs mt-1">{t('matchIdCannotBeEmpty', language) || 'Match ID cannot be empty'}</p>
              )}
              
              {newMatchId.trim().length > 50 && (
                <p className="text-red-400 text-xs mt-1">{t('matchIdTooLong', language) || 'Match ID is too long'}</p>
              )}
              
              {newMatchId.trim() === tournament.matchId && newMatchId.trim().length > 0 && (
                <p className="text-yellow-400 text-xs mt-1">{t('sameAsCurrentMatchId', language) || 'Same as current match ID'}</p>
              )}
              
              {isValid && newMatchId.trim() !== tournament.matchId && newMatchId.trim().length > 0 && (
                <p className="text-green-400 text-xs mt-1">{t('validNewMatchId', language) || 'Valid new match ID'}</p>
              )}
            </div>

            {/* Room URL Field (Optional) */}
            <div className="role-reason-group" style={{ marginTop: '16px' }}>
              <label className="role-reason-label">
                {t('roomUrl', language) || 'Room URL'} <span className="text-gray-400 text-xs">({t('optional', language) || 'Optional'})</span>
              </label>
              <input
                type="url"
                value={roomUrl}
                onChange={handleRoomUrlChange}
                placeholder={t('enterRoomUrl', language) || 'https://... (optional link to join room)'}
                className="role-search-input"
                disabled={loading}
              />
              <p className="text-gray-400 text-xs mt-1">
                {t('roomUrlDescription', language) || 'If provided, players will see a "Join Room" button'}
              </p>
            </div>

            {tournament.matchIdHistory && tournament.matchIdHistory.length > 1 && (
              <div className="modal-reason">
                <p className="modal-reason-title">{t('matchIdHistory', language) || 'Match ID History'}</p>
                <div className="text-xs space-y-1 max-h-20 overflow-y-auto">
                  {tournament.matchIdHistory.slice(-3).reverse().map((id, index) => (
                    <p key={index} className="modal-reason-text font-mono">
                      {index === 0 ? '→ ' : '   '}{id} {index === 0 && ` (${t('current', language) || 'current'})`}
                    </p>
                  ))}
                  {tournament.matchIdHistory.length > 3 && (
                    <p className="modal-reason-text text-gray-400">... {t('more', language) || 'more'} {tournament.matchIdHistory.length - 3}</p>
                  )}
                </div>
              </div>
            )}

            <div className="modal-warning">
              <p className="modal-warning-text">
                {t('changingMatchIdNotification', language) || 'Changing the match ID will notify all participants'}
              </p>
            </div>
          </form>
        </div>
        
        <div className="modal-actions">
          <button
            onClick={handleSubmit}
            disabled={loading || !isValid || !hasChanges()}
            className="modal-btn modal-btn-primary"
          >
            {loading ? t('updating', language) || 'Updating...' : t('update', language) || 'Update'}
          </button>
          <button
            onClick={onCancel}
            disabled={loading}
            className="modal-btn modal-btn-secondary"
          >
            {t('cancel', language)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveTab;